import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth, useClerk } from '@clerk/clerk-react';
import { api } from '../utils/api';
import { useAdmin } from '../hooks/useAdmin';

export default function Admin() {
  const { getToken } = useAuth();
  const { signOut } = useClerk();
  const { isAdmin, loading: checking } = useAdmin();
  const [error, setError] = useState(null);
  const [sales, setSales] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [salesLoading, setSalesLoading] = useState(false);
  const [earningsLoading, setEarningsLoading] = useState(false);
  const [timePeriod, setTimePeriod] = useState('all'); // 'all', 'day', 'week', 'month'
  const location = useLocation();
  const isReportsView = location.pathname === '/admin/reports';
  const [cities, setCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [selectedCity, setSelectedCity] = useState(null); // city detail for viewing garage sellers
  const [garageSellers, setGarageSellers] = useState([]);
  const [garageSellersLoading, setGarageSellersLoading] = useState(false);
  const [garageSellerToDelete, setGarageSellerToDelete] = useState(null); // garage seller to delete
  const [deletingGarageSeller, setDeletingGarageSeller] = useState(false);

  // Load cities when admin (for Dashboard tab). Refetch when opening Dashboard.
  useEffect(() => {
    if (!isAdmin) {
      setCities([]);
      return;
    }
    // Only fetch when on Dashboard so we have fresh list when user opens it
    if (location.pathname !== '/admin') return;

    let cancelled = false;
    (async () => {
      setCitiesLoading(true);
      try {
        const res = await api('/api/admin/markets', { getToken });
        const data = await res.json().catch(() => null);
        if (!cancelled) {
          const list = Array.isArray(data) ? data : (data?.data && Array.isArray(data.data) ? data.data : []);
          setCities(list);
          if (res.ok) setError(null);
          else if (list.length === 0) setError(data?.error || `Could not load cities (${res.status})`);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load cities:', err);
          setCities([]);
        }
      } finally {
        if (!cancelled) setCitiesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin, getToken, location.pathname]);

  // Load sales and earnings when admin status is confirmed or time period changes
  useEffect(() => {
    if (!isAdmin) {
      setSales([]);
      setEarnings(null);
      return;
    }

    let cancelled = false;

    // Build query string with period parameter
    const periodParam = timePeriod !== 'all' ? `?period=${timePeriod}` : '';

    // Load sales
    (async () => {
      setSalesLoading(true);
      try {
        const res = await api(`/api/admin/sales${periodParam}`, { getToken });
        if (!res.ok) throw new Error(`Failed to load sales: ${res.status}`);
        const data = await res.json();
        if (!cancelled) setSales(data);
      } catch (err) {
        if (!cancelled) console.error('Failed to load sales:', err);
      } finally {
        if (!cancelled) setSalesLoading(false);
      }
    })();

    // Load earnings
    (async () => {
      setEarningsLoading(true);
      try {
        const res = await api(`/api/admin/earnings${periodParam}`, { getToken });
        if (!res.ok) throw new Error(`Failed to load earnings: ${res.status}`);
        const data = await res.json();
        if (!cancelled) setEarnings(data);
      } catch (err) {
        if (!cancelled) console.error('Failed to load earnings:', err);
      } finally {
        if (!cancelled) setEarningsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [isAdmin, getToken, timePeriod]);

  const handleExportCsv = async () => {
    try {
      const res = await api('/api/admin/sales/export-csv', { getToken });
      if (!res.ok) {
        throw new Error(`Failed to export CSV: ${res.status}`);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sales-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(err.message || 'Failed to export CSV.');
    }
  };

  const handleOpenCity = async (city) => {
    const cityId = city.id ?? city.Id;
    setSelectedCity(city);
    setGarageSellersLoading(true);
    setError(null);
    try {
      const res = await api(`/api/admin/cities/${cityId}/garage-sellers`, { getToken });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to load garage sellers (${res.status})`);
      }
      const data = await res.json();
      setGarageSellers(data);
    } catch (err) {
      console.error('Error loading garage sellers:', err);
      setError(err.message || 'Failed to load garage sellers.');
      setGarageSellers([]);
    } finally {
      setGarageSellersLoading(false);
    }
  };

  const handleCloseCityModal = () => {
    setSelectedCity(null);
    setGarageSellers([]);
    setGarageSellerToDelete(null);
    setError(null);
  };

  const handleDeleteGarageSeller = async () => {
    if (!garageSellerToDelete) return;
    const garageSellerId = garageSellerToDelete.id ?? garageSellerToDelete.Id;
    setDeletingGarageSeller(true);
    setError(null);
    try {
      const res = await api(`/api/admin/garage-sellers/${garageSellerId}`, { method: 'DELETE', getToken });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Failed to delete (${res.status})`);
      }
      // Remove from list and update city count
      setGarageSellers(prev => prev.filter(gs => (gs.id ?? gs.Id) !== garageSellerId));
      setCities(prev => prev.map(c => {
        const id = c.id ?? c.Id;
        if (id === (selectedCity?.id ?? selectedCity?.Id)) {
          const currentCount = c.currentVendorCount ?? c.CurrentVendorCount ?? c.garageSellerCount ?? 0;
          return { ...c, currentVendorCount: Math.max(0, currentCount - 1), garageSellerCount: Math.max(0, currentCount - 1) };
        }
        return c;
      }));
      setGarageSellerToDelete(null);
    } catch (err) {
      setError(err.message || 'Failed to delete garage seller.');
    } finally {
      setDeletingGarageSeller(false);
    }
  };

  if (checking) {
    return (
      <div className="flex items-center justify-center py-12 text-slate-500 dark:text-slate-400">
        Checking permissions…
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="max-w-md mx-auto p-8 text-center">
        <h2 className="text-2xl font-semibold text-slate-800 dark:text-slate-200 mb-2">Access denied</h2>
        <p className="text-slate-600 dark:text-slate-400">
          You don’t have permission to view this page.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <header className="flex items-center justify-between gap-4 mb-6">
        <h1 className="text-4xl font-bold">{isReportsView ? 'Reports' : 'Dashboard'}</h1>
        <button
          type="button"
          onClick={() => signOut({ redirectUrl: '/' })}
          className="shrink-0 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 underline underline-offset-2"
        >
          Sign out
        </button>
      </header>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Dashboard: Cities list (route /admin) */}
      {!isReportsView && (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-semibold mb-4">Cities</h2>
          {citiesLoading ? (
            <div className="text-center py-8 text-slate-500">Loading cities…</div>
          ) : cities.length === 0 ? (
            <div className="text-center py-8 text-slate-500">No cities found.</div>
          ) : (
            <ul className="divide-y divide-slate-200 dark:divide-slate-700">
              {cities.map((c) => {
                const id = c.id ?? c.Id;
                const name = c.name ?? c.Name ?? 'Unnamed';
                const garageSellerCount = c.currentVendorCount ?? c.CurrentVendorCount ?? c.garageSellerCount ?? 0;
                return (
                  <li key={id}>
                    <button
                      type="button"
                      onClick={() => handleOpenCity(c)}
                      className="w-full text-left py-4 px-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors"
                    >
                      <div className="font-semibold text-lg text-slate-900 dark:text-slate-100">{name}</div>
                      <div className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                        Garage Sales: {garageSellerCount}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {/* Reports: Sales & Earnings (route /admin/reports) */}
      {isReportsView && (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-semibold">Sales & Earnings</h2>
            <button
              type="button"
              onClick={handleExportCsv}
              className="text-lg font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 underline underline-offset-2 hover:no-underline"
            >
              Export to CSV
            </button>
          </div>
          
          {/* Time Period Selector */}
          <div className="mb-6 flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setTimePeriod('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                timePeriod === 'all'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600'
              }`}
            >
              All Time
            </button>
            <button
              type="button"
              onClick={() => setTimePeriod('month')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                timePeriod === 'month'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600'
              }`}
            >
              This Month
            </button>
            <button
              type="button"
              onClick={() => setTimePeriod('week')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                timePeriod === 'week'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600'
              }`}
            >
              This Week
            </button>
            <button
              type="button"
              onClick={() => setTimePeriod('day')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                timePeriod === 'day'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600'
              }`}
            >
              Today
            </button>
          </div>
          {earningsLoading ? (
            <div className="text-center py-8 text-slate-500">Loading earnings…</div>
          ) : earnings ? (
            <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <div className="text-sm text-blue-700 dark:text-blue-300">Total Sales</div>
                <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">{earnings.totalSalesCount}</div>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                <div className="text-sm text-green-700 dark:text-green-300">Total Gross Sales</div>
                <div className="text-2xl font-bold text-green-900 dark:text-green-100">${earnings.totalGrossSales.toFixed(2)}</div>
              </div>
              <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border-2 border-purple-300 dark:border-purple-700">
                <div className="text-sm text-purple-700 dark:text-purple-300">Platform Earnings (5%)</div>
                <div className="text-3xl font-bold text-purple-900 dark:text-purple-100">${earnings.totalPlatformEarnings.toFixed(2)}</div>
              </div>
            </div>
          ) : null}
          {salesLoading ? (
            <div className="text-center py-8 text-slate-500">Loading sales…</div>
          ) : sales.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-sm md:text-base">
                <thead>
                  <tr className="border-b-2 border-slate-300 dark:border-slate-600">
                    <th className="p-3 font-semibold">Date</th>
                    <th className="p-3 font-semibold">Item</th>
                    <th className="p-3 font-semibold">Vendor</th>
                    <th className="p-3 font-semibold">Garage Sale</th>
                    <th className="p-3 font-semibold">Buyer</th>
                    <th className="p-3 font-semibold">Amount</th>
                    <th className="p-3 font-semibold">Platform Fee</th>
                    <th className="p-3 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.map((sale) => (
                    <tr key={sale.id} className="border-b border-slate-200 dark:border-slate-700">
                      <td className="p-3">{new Date(sale.createdAt).toLocaleDateString()}</td>
                      <td className="p-3"><div className="font-medium">{sale.itemTitle}</div><div className="text-xs text-slate-500">ID: {sale.itemId}</div></td>
                      <td className="p-3">{sale.vendorStoreName ? `${sale.vendorStoreName} Garage sale` : sale.vendorStoreName}</td>
                      <td className="p-3">{sale.marketName ? `${sale.marketName} Garage sale` : sale.marketName}</td>
                      <td className="p-3">{sale.buyerEmail || 'N/A'}</td>
                      <td className="p-3 font-medium">${sale.amount.toFixed(2)}</td>
                      <td className="p-3 text-purple-600 dark:text-purple-400">${sale.platformFee.toFixed(2)}</td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded text-xs ${sale.status === 'picked_up' ? 'bg-green-100 dark:bg-green-900/30 text-green-800' : sale.status === 'paid' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800' : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800'}`}>
                          {sale.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">No sales data available.</div>
          )}
        </div>
      )}


      {/* Garage Sellers Modal — shows garage sellers for selected city */}
      {selectedCity && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={handleCloseCityModal}>
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[calc(100dvh-2rem)] overflow-y-auto overflow-x-hidden" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 p-6 pb-4 z-10">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                  {selectedCity.name ?? selectedCity.Name}
                </h3>
                <button
                  type="button"
                  onClick={handleCloseCityModal}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                  aria-label="Close"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {garageSellers.length} garage sale{garageSellers.length !== 1 ? 's' : ''} in this city
              </p>
            </div>

            <div className="p-6 pt-4">
              {garageSellersLoading ? (
                <div className="text-center py-12 text-slate-500">Loading garage sales…</div>
              ) : garageSellers.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <svg className="w-16 h-16 mx-auto mb-4 text-slate-300 dark:text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                  <p className="text-lg font-medium">No garage sales found</p>
                  <p className="text-sm mt-1">This city doesn't have any garage sales yet.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {garageSellers.map((gs) => {
                    const id = gs.id ?? gs.Id;
                    const storeName = gs.storeName ?? gs.StoreName ?? 'Unnamed';
                    const address = gs.address ?? gs.Address ?? 'No address';
                    const phone = gs.phone ?? gs.Phone;
                    const itemCount = gs.itemCount ?? gs.ItemCount ?? 0;
                    const createdAt = gs.createdAt ? new Date(gs.createdAt).toLocaleDateString() : 'Unknown';

                    return (
                      <div
                        key={id}
                        className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4 border border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-1 truncate">
                              {storeName !== 'Unnamed' ? `${storeName} Garage sale` : storeName}
                            </h4>
                            <div className="space-y-1 text-sm text-slate-600 dark:text-slate-400">
                              {address && (
                                <div className="flex items-center gap-2">
                                  <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                  </svg>
                                  <span className="truncate">{address}</span>
                                </div>
                              )}
                              {phone && (
                                <div className="flex items-center gap-2">
                                  <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                  </svg>
                                  <span>{phone}</span>
                                </div>
                              )}
                              <div className="flex items-center gap-4 mt-2 pt-2 border-t border-slate-200 dark:border-slate-600">
                                <div className="flex items-center gap-1">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                  </svg>
                                  <span>{itemCount} item{itemCount !== 1 ? 's' : ''}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                  <span>Created {createdAt}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setGarageSellerToDelete(gs)}
                            className="shrink-0 w-10 h-10 flex items-center justify-center rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                            aria-label={`Delete ${storeName}`}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {garageSellerToDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center shrink-0">
                <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100">Delete Garage Sale?</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">This action cannot be undone.</p>
              </div>
            </div>

            <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4 mb-6">
              <p className="text-sm font-medium text-slate-900 dark:text-slate-100 mb-2">
                {(garageSellerToDelete.storeName ?? garageSellerToDelete.StoreName ?? 'Unnamed') !== 'Unnamed' 
                  ? `${garageSellerToDelete.storeName ?? garageSellerToDelete.StoreName ?? 'Unnamed'} Garage sale`
                  : 'Unnamed'}
              </p>
              {garageSellerToDelete.address && (
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {garageSellerToDelete.address}
                </p>
              )}
              <p className="text-xs text-slate-500 dark:text-slate-500 mt-2">
                This will permanently delete this garage sale and all associated items.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setGarageSellerToDelete(null)}
                disabled={deletingGarageSeller}
                className="flex-1 h-11 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors font-medium disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteGarageSeller}
                disabled={deletingGarageSeller}
                className="flex-1 h-11 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deletingGarageSeller ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
